<template>
  <ClientOnly>
    <div v-if="!isLoading" ref="pdfContent" class="pdfContent">
      <div id="dashboardWrap" ref="dashboardWrap">
        <main id="dashboardContent" class="pt0">
          <LazyProSkillManagerCommonBreadCrumb :path-name="assessmentDetailInfo.title" style="top: 0; padding: 13px 40px"/>
          <LazyProSkillManagerAssessmentDetailBanner
            :assessment-info="assessmentDetailInfo"
            :assessment-id="assessmentId"
            :is-pdf-style="true"
            style="padding: 39px 24px;"
          />
          <LazyProSkillManagerAssessmentDetailTab :tab-type="ReportTabType.ASSESSMENT" style="top: 0; background: #EAECF3; padding: 0 24px"/>
          <div class="contentLineWrap" style="padding: 0 24px; margin-bottom: 60px;">
            <div class="containerDash-1480 flex">
              <section id="skillMonitor" style="width: 100%">
                <div class="containerDash" style="padding: 0; margin: 0; max-width: 1480px">
                  <div class="skillMonitor">
                    <LazyProSkillManagerAssessmentDetailContentAssessmentSummary :assessment-summary="assessmentSummary"/>
                    <section
                      v-for="(track, index) in trackSummary"
                      :key="track.trackSummary.trackId"
                      class="reportItem flex col reportBreakBefore"
                    >
                      <div class="titArea flex">
                        <h3>{{ track.trackSummary.trackName }}</h3>
                      </div>
                      <div class="flex col gap-16">
                        <LazyCommonResultTrackSummary
                          :track-summary="track"
                          :is-assessment-detail-report="true"
                          :assessment-id="assessmentId"
                          :is-pdf="true"
                        />
                        <LazyCommonResultSkillSummary
                          class="reportBreakBefore"
                          :is-empty-candidate="assessmentSummary.candidatesSummary.total === 0"
                          :skill-summary="skillSummary[index]"
                          :is-assessment-detail-report="true"
                          :is-pdf="true"
                        />
                      </div>
                    </section>
                  </div>
                </div>
              </section>
            </div>
          </div>
          <LazyProSkillManagerAssessmentDetailTab class="reportBreakBefore" :tab-type="ReportTabType.CANDIDATE" style="top: 0; background: #EAECF3; padding: 0 24px"/>
          <div class="contentLineWrap" style="padding: 0 24px; margin-bottom: 60px;">
            <div class="containerDash-1480 flex">
              <section id="skillMonitor" style="width: 100%">
                <div class="containerDash" style="padding: 0; margin: 0; max-width: 1480px">
                  <div class="skillMonitor">
                    <LazyProSkillManagerAssessmentDetailContentCandidateSummary
                      :candidate-summary="candidateSummary"
                      :candidate-integrity-page-data="candidateIntegrityPageData"
                      :is-pdf="true"
                    />
                    <LazyProSkillManagerAssessmentDetailContentCandidateList
                      class="reportBreakBefore"
                      :assessment-id="assessmentId"
                      :is-pdf="true"
                    />
                  </div>
                </div>
              </section>
            </div>
          </div>
        </main>
      </div>
    </div>
  </ClientOnly>
</template>

<script setup lang="ts">
import {
  LazyCommonResultSkillSummary,
  LazyCommonResultTrackSummary,
  LazyProSkillManagerAssessmentDetail,
  LazyProSkillManagerAssessmentDetailBanner,
  LazyProSkillManagerAssessmentDetailContentAssessmentSummary,
  LazyProSkillManagerAssessmentDetailContentCandidateList, LazyProSkillManagerAssessmentDetailContentCandidateSummary,
  LazyProSkillManagerAssessmentDetailTab,
  LazyProSkillManagerCommonBreadCrumb,
  LazyProSkillManagerCommonScrollDepthMenu
} from "#components";
import { computed, onMounted, ref } from "vue";
import { useRoute } from "#imports";
import {
  ProSkillManagerAssessmentDetailInfoModel
} from "~~/package/mapper/proSkillManager/model/assessment/ProSkillManagerAssessmentDetailInfoModel";
import {
  ProSkillManagerAssessmentCandidateSummaryModel
} from "~~/package/mapper/proSkillManager/model/candidate/ProSkillManagerAssessmentCandidateSummaryModel";
import { AssessmentSummaryModel } from "~~/package/mapper/report/model/assessment/AssessmentSummaryModel";
import type { TrackSummaryModel } from "~~/package/mapper/report/model/assessment/TrackSummaryModel";
import type { SkillSummaryModel } from "~~/package/mapper/report/model/assessment/SkillSummaryModel";
import { useProSkillManagerService } from "~/composables/service/useProSkillManagerService";
import { useReportService } from "~/composables/service/useReportService";
import { ReportDomainType } from "~~/package/mapper/report/model/ReportDomainType";
import {
  ProSkillManagerCandidateModel
} from "~~/package/mapper/proSkillManager/model/candidate/ProSkillManagerCandidateModel";
import { PageModel } from "~~/package/mapper/common/model/PageModel";
import {
  ProSkillManagerCandidateStatusType
} from "~~/package/mapper/proSkillManager/model/candidate/ProSkillManagerCandidateStatusType";
import { ReportTabType } from "~~/package/mapper/report/model/ReportTabType";

const { $i18n } = useNuxtApp();
const lang = computed(() => $i18n.locale.value.toUpperCase());
const route = useRoute();

const proSkillManagerService = useProSkillManagerService();
const reportService = useReportService();

const assessmentId = computed(() => parseInt(route.params.assessmentId as string));
const isLoading = ref(true);

const assessmentDetailInfo = ref(new ProSkillManagerAssessmentDetailInfoModel());
const assessmentSummary = ref(new AssessmentSummaryModel());
const trackSummary = ref<TrackSummaryModel[]>([]);
const skillSummary = ref<SkillSummaryModel[]>([]);
const candidateSummary = ref(new ProSkillManagerAssessmentCandidateSummaryModel());
const candidateIntegrityPageData = ref<PageModel<ProSkillManagerCandidateModel>>(new PageModel());

const initData = async () => {
  try {
    const [
      detailInfo, summary, tracks, skills, candidateSum, candidateList
    ] = await Promise.all([
      proSkillManagerService.getProSkillManagerAssessmentDetailInfo(assessmentId.value),
      reportService.getReportAssessmentSummary(ReportDomainType.CUSTOM_ASSESSMENT, assessmentId.value),
      reportService.getReportTrackSummary(ReportDomainType.CUSTOM_ASSESSMENT, assessmentId.value),
      reportService.getReportSkillSummary(lang.value, ReportDomainType.CUSTOM_ASSESSMENT, assessmentId.value),
      proSkillManagerService.getProSkillManagerCandidateSummary(lang.value, assessmentId.value),
      proSkillManagerService.getProSkillManagerCandidate(
        lang.value,
        1,
        999999,
        "latest,desc",
        assessmentId.value,
        "integrity=true",
        [ProSkillManagerCandidateStatusType.COMPLETED]
      )
    ]);

    assessmentDetailInfo.value = detailInfo;
    assessmentSummary.value = summary;
    trackSummary.value = tracks;
    skillSummary.value = skills;
    candidateSummary.value = candidateSum;
    candidateIntegrityPageData.value = candidateList;

  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  initData();
});
</script>

<style>
@import "assets/scss/business_style.scss";

.pdfContent .reportBreakBefore {
  break-before: page !important;
  page-break-before: always !important;
}

.pdfContent .board__detail {
  break-inside: avoid;
}
.pdfContent .reportBreakInside {
  break-inside: avoid;
  page-break-inside: avoid; /* 구형 호환 */
}
.pdfContent .reportBreakInsideSkip {
  break-inside: auto !important;
  page-break-inside: auto !important; /* 구형 호환 */
}
</style>